/**
 * Public API for the UI sound system.
 */

import { UiSoundName } from "./uiSfx.types";
import { getAudio, fileStatus } from "./uiSfx.files";
import { playSynth } from "./uiSfx.synth";

// --- public API ----------------------------------------------------------

/**
 * Play a named UI sound. Prefers a real mp3 from src/assets/audio/ui/<name>.mp3
 * when it is a non-empty file; otherwise plays the WebAudio synth fallback.
 */
export function playUiSound(name: UiSoundName) {
  if (typeof window === "undefined") return;

  const audio = getAudio(name);
  const status = fileStatus[name];

  // If we already know the file is empty, skip straight to synth.
  if (status === "empty" || !audio) {
    playSynth(name);
    return;
  }

  // If the file is loaded and usable, play it.
  if (
    status === "usable" ||
    (audio.readyState >= 2 &&
      Number.isFinite(audio.duration) &&
      audio.duration > 0.02)
  ) {
    try {
      audio.currentTime = 0;
      const p = audio.play();
      if (p) p.catch(() => playSynth(name));
      return;
    } catch {
      playSynth(name);
      return;
    }
  }

  // Status still "unknown" (metadata not loaded yet). Play synth now for
  // snappy feedback; subsequent calls will use the real file once loaded.
  playSynth(name);
}

/**
 * Pick an appropriate error sound based on an error message / code.
 */
export function pickErrorSound(input?: string | Error | null): UiSoundName {
  if (!input) return "buzz";
  const msg = (typeof input === "string" ? input : input.message || "")
    .toLowerCase();
  if (
    msg.includes("insufficient") ||
    msg.includes("not enough") ||
    msg.includes("balance") ||
    msg.includes("collateral")
  )
    return "thud";
  if (
    msg.includes("network") ||
    msg.includes("timeout") ||
    msg.includes("fetch") ||
    msg.includes("rate limit") ||
    msg.includes("429") ||
    msg.includes("502") ||
    msg.includes("503") ||
    msg.includes("504") ||
    msg.includes("connection")
  )
    return "warble";
  if (
    msg.includes("invalid") ||
    msg.includes("required") ||
    msg.includes("must be") ||
    msg.includes("too small") ||
    msg.includes("too large") ||
    msg.includes("min ") ||
    msg.includes("max ")
  )
    return "pop";
  return "buzz";
}
